/*
  Загрузка реестра должников - первый шаг работы с портфелем (30.09.2026).

  Решение владельца: реестр стал отправной точкой. Из него заводятся лицевые
  счета и должники, и только потом отчёт по начислениям приносит суммы по
  месяцам, из которых считаются давность, пени и цена требования.

  🔴 Выбор файла стоит ПЕРВЫМ, до памятки (макет владельца 30.09.2026). Человек
  открывает раздел, чтобы загрузить файл, а не читать инструкцию: памятка ему
  нужна один раз, а поле - каждый раз.

  🔴 Памятка честно говорит, чего мы из файла НЕ берём. «Долг всего», «Истёк срок
  ИД» и «Можно взыскать» посчитала управляющая компания на дату выгрузки, а
  давность и пени считает правовое ядро помесячно и на дату подачи. Промолчи
  экран об этом - человек вправе ждать, что его цифра уйдёт в заявление.

  Два обращения на загрузку: сперва «как система поняла файл», потом запись по
  подтверждению.

  🔴 С 20.10.2026 файл уходит на сервер ОДИН раз и разбирается один раз
  (решение владельца 19.10.2026, `useUploadFlow`): выбор файла сразу отправляет
  его, а «Посмотреть», способы листов, окно счетов и запись называют уже
  прочитанный файл номером. Запись - фоновой работой с ходом, конец - зелёный
  блок «Готово»; план по листам и строкам остаётся под ним.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { Notice } from './Common.jsx'
import PickAccounts from './picker/PickAccounts.jsx'
import RegistryIssues from './RegistryIssues.jsx'
import RegistryPlan from './RegistryPlan.jsx'
import RegistryRows from './RegistryRows.jsx'
import RegistrySheets from './RegistrySheets.jsx'
import UploadEnd from './upload/UploadEnd.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'
import UploadHelp from './UploadHelp.jsx'

/*
  Образец файла - он же картинка над кнопкой «Скачать образец». Строки взяты из
  настоящего реестра заказчика (ООО «УПК «Ресурс», приложение № 1 к договору),
  поэтому человек видит не выдуманный вид, а тот самый, который получится у него.
*/
const SAMPLE = {
  file: 'реестр-образец.csv',
  headers: ['Лицевой счёт', 'Дом', 'Адрес', 'Ф.И.О.', 'Вид собственности'],
  rows: [
    ['12780', '23', 'Дом 23, кв. 81', 'Павленко Антонина Павловна', 'Частная'],
    ['28113', '28', 'Дом 28, кв. 114', 'Калинин Алексей Алексеевич', 'Муниципальная'],
  ],
}

/* Выбор человека - теми же полями, что уходили формой записи; пустое не уходит. */
function writeParams(treatments, only) {
  return {
    column_overrides: null,
    sheet_treatments: Object.keys(treatments).length ? JSON.stringify(treatments) : null,
    only_accounts: only ? JSON.stringify(only) : null,
  }
}

export default function RegistryUpload({ onLoaded }) {
  const flow = useUploadFlow('registry')
  const file = flow.stored
  const [plan, setPlan] = useState(null)
  const [treatments, setTreatments] = useState({})
  // Счета из окна «Какие счета загрузить» (16.10.2026); пусто - весь файл.
  const [only, setOnly] = useState(null)
  const [written, setWritten] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Номер захода: новый заход пересоздаёт поле выбора без имени прежнего файла.
  const [round, setRound] = useState(0)

  function choose(chosen) {
    setPlan(null)
    setTreatments({})
    setOnly(null)
    setWritten(false)
    setError(null)
    flow.choose(chosen ?? null)
  }

  function again() {
    choose(null)
    setRound(round + 1)
  }

  // Возвращает «получилось»: кнопке выбора счетов нужно знать, открывать ли окно.
  async function act(action, then) {
    setBusy(true)
    setError(null)
    try {
      then(await action())
      return true
    } catch (failure) {
      setError(failure)
      return false
    } finally {
      setBusy(false)
    }
  }

  const look = () =>
    act(
      () =>
        flow.check(
          () => api.previewRegistry(file, null, treatments, only),
          // Итог этапа короче плана под ним и не повторяет его фразу (20.10.2026).
          (seen) => `строк в файле: ${seen.total}, счетов к заведению: ${seen.to_create}`,
        ),
      setPlan,
    )

  // Другой способ листа или другой выбор счетов - другой предпросмотр: человек
  // подтверждает ровно то, что запишется, а не то, что было до его выбора.
  // Выбор принимается только вместе с ответом сервера (аудит 28.10.2026, раздел 5.3): при сбое
  // на экране оставался прежний способ листа, а записан был бы новый.
  const retreat = (title, treatment) => {
    const next = { ...treatments, [title]: treatment }
    act(
      () => api.previewRegistry(file, null, next, only),
      (seen) => {
        setTreatments(next)
        setPlan(seen)
      },
    )
  }
  const pick = (numbers) => {
    act(
      () => api.previewRegistry(file, null, treatments, numbers),
      (seen) => {
        setOnly(numbers)
        setPlan(seen)
      },
    )
  }

  // Запись - фоновой работой по уже прочитанному файлу (20.10.2026); ответ тот
  // же, что отдавал запрос записи: сервер вызывает тот же обработчик.
  const load = () =>
    act(
      () => flow.write('registry', writeParams(treatments, only)),
      (done) => {
        setPlan(done)
        setWritten(true)
        // Чек-лист готовности считает собственников: после записи он обязан
        // пересчитаться, иначе человек видит «нет» по только что заведённому.
        // Номер загрузки нужен журналу шага - он ждёт ИМЕННО эту строку.
        onLoaded(done.import_run_id)
      },
    )

  return (
    <>
      <section className="section card">
        <div className="pickfile">
          <label className="pickfile__control">
            <span className="pickfile__label">Файл реестра</span>
            <span className="pickfile__note">одна строка на лицевой счёт, CSV или XLSX</span>
            <input
              key={`${round}-${flow.attempt}`}
              className="field__file pickfile__input"
              type="file"
              accept=".csv,.xlsx"
              disabled={busy || flow.busy}
              onChange={(event) => choose(event.target.files?.[0])}
            />
          </label>
        </div>
        <UploadStages flow={flow} />

        <div className="row">
          <button type="button" className="button" disabled={busy || !file || written} onClick={look}>
            Посмотреть, что получится
          </button>
          <PickAccounts
            choices={plan?.choices ?? null}
            only={only}
            onChange={pick}
            onNeed={look}
            disabled={busy || !file || written}
            note={file ? `${file.name} · счетов в файле: ${plan?.choices.length ?? '…'}` : null}
            amountTitle="Долг по расчёту УК"
          />
          <button
            type="button"
            className="button"
            disabled={busy || !file || !plan || written}
            onClick={load}
          >
            Завести в портфель
          </button>
        </div>

        <Notice error={flow.error ?? error} />
        {/* Конец - блок «Готово» (20.10.2026) над планом: план по листам и
            строкам остаётся под ним, его смотрят после записи так же, как до. */}
        {written ? (
          <UploadEnd
            title="Готово: счета заведены в портфель"
            facts={[
              ['Строк в файле', plan.total],
              ['Заведено счетов', plan.to_create],
              ['Уже в портфеле', plan.existing || null],
              ['Строк, оставшихся вам', plan.issues.length],
            ]}
            seconds={flow.written?.seconds}
            onAgain={again}
          />
        ) : null}
        {plan ? <RegistryPlan plan={plan} /> : null}
        {plan ? <RegistrySheets sheets={plan.sheets} onChange={retreat} busy={busy || written} /> : null}
        {plan ? <RegistryRows rows={plan.rows} sheets={plan.sheets} /> : null}
        {/* Замечания - ПОД плитками и таблицей (осмотр 05.10.2026): над ними они
            уводили плитки за край экрана. */}
        {plan ? <RegistryIssues issues={plan.issues} skipped={plan.skipped_rows ?? []} /> : null}
      </section>

      <UploadHelp
        title="Где это взять и как должен выглядеть файл"
        where={[
          'Реестр передаваемой задолженности готовит управляющая компания - это приложение ' +
            'к договору: одна строка на лицевой счёт, а в ней счёт, дом, адрес, Ф.И.О. ' +
            'и вид собственности.',
          'Шапка таблицы может стоять не в первой строке: название приложения над ней ' +
            'Арбитр ЖКХ пропускает и говорит, сколько строк пропустил.',
          'Если счета разложены по листам - например, «На проверку» или «Исключены», - ' +
            'Арбитр ЖКХ прочитает все листы и предложит, как загрузить каждый. Отметки ' +
            'вида «просужен до 30.09.2022» читаются в любой колонке примечаний: эти месяцы ' +
            'в заявление не пойдут.',
        ]}
        sample={SAMPLE}
        note={
          'Обязательны лицевой счёт, Ф.И.О. и адрес: реестр заводит счёт и должника, ' +
          'и без них строка не значит ничего, что можно записать. Вид собственности ' +
          'определяет, кто отвечает по долгу, - счёт с нераспознанным видом не заводится. ' +
          'Графы «Долг всего», «Истёк срок ИД» и «Можно взыскать» показываются для сверки, ' +
          'но в заявление не переносятся: давность и пени Арбитр ЖКХ считает сам, ' +
          'помесячно и на дату подачи.'
        }
      />
    </>
  )
}
